"use client";

/**
 * Hook for managing CRM bindings for agents.
 * Uses the new CRM API routes.
 *
 * Types are imported from lib/crm/types.ts to avoid duplication.
 */

import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { agentDeliverySettingsQueryKey } from "@/hooks/queries/delivery-bindings";
import { agentIntegrationStatusesQueryKey } from "@/hooks/queries/agent-integration-status-keys";
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import type { DeliveryScopeType } from "@/lib/deliveries/config";
import type {
  CrmProviderType,
  CrmScopeType,
  CrmPipeline,
  CrmStage,
  CrmCapabilities,
  CrmBindingConfig,
} from "@/lib/crm/types";
import { useTranslations } from "next-intl"


// Re-export types for consumers
export type {
  CrmProviderType,
  CrmScopeType,
  CrmPipeline,
  CrmStage,
  CrmCapabilities,
  CrmBindingConfig,
};

// Types specific to UI/hooks (not in lib/crm/types.ts)
export interface CrmIntegration {
  id: string;
  tenantId: string;
  provider: CrmProviderType;
  name: string;
  isActive: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CrmBinding {
  id: string;
  tenantId: string;
  integrationId: string;
  scopeType: CrmScopeType;
  scopeId: string | null;
  config: CrmBindingConfig;
  createdAt: string;
  updatedAt: string;
}

export interface CrmProvider {
  id: string;
  name: string;
  implemented: boolean;
  capabilities: CrmCapabilities;
}

interface UseCrmBindingOptions {
  scopeType: CrmScopeType;
  scopeId: string;
  autoLoad?: boolean;
}

export function useCrmBinding({ scopeType, scopeId, autoLoad = true }: UseCrmBindingOptions) {
  const tUi = useTranslations("Feedback.IntegrationsUseCrmBinding")

  const queryClient = useQueryClient();
  const { fetchWithAuth, isAuthenticated, user } = useAuth();
  const tenantId = getQueryTenantId(user);

  // State
  const [binding, setBinding] = useState<CrmBinding | null>(null);
  const [integrations, setIntegrations] = useState<CrmIntegration[]>([]);
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [providers, setProviders] = useState<CrmProvider[]>([]);
  // Tenant default binding info (when no agent-specific binding exists)
  const [tenantDefault, setTenantDefault] = useState<{
    integrationId: string;
    enabled: boolean;
  } | null>(null);
  const [hasTenantDefault, setHasTenantDefault] = useState(false);

  const [isLoadingBinding, setIsLoadingBinding] = useState(false);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidateDeliverySettings = useCallback(async () => {
    const deliveryScopeType = getDeliveryScopeType(scopeType);
    if (!deliveryScopeType) return;

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: agentDeliverySettingsQueryKey(
          tenantId,
          deliveryScopeType,
          scopeId,
        ),
      }),
      queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesQueryKey(
          tenantId,
          deliveryScopeType,
        ),
      }),
    ]);
  }, [queryClient, scopeId, scopeType, tenantId]);

  // Load binding for scope
  const loadBinding = useCallback(async () => {
    if (!isAuthenticated || !scopeId) return;

    setIsLoadingBinding(true);
    setError(null);

    try {
      const res = await fetchWithAuth(
        `/api/crm/bindings/scope?scope_type=${scopeType}&scope_id=${scopeId}&include_integrations=true`
      );

      if (res.ok) {
        const data = await res.json();
        setBinding(data.binding || null);
        // Set tenant default info
        setTenantDefault(data.tenantDefault || null);
        setHasTenantDefault(data.hasTenantDefault || false);
        if (data.integrations) {
          setIntegrations(data.integrations);
        }
      } else {
        const errData = await res.json();
        setError(errData.error || "Failed to load CRM binding");
      }
    } catch (err) {
      console.error("Error loading CRM binding:", err);
      setError(tUi("failedToLoadCRMBinding"));
    } finally {
      setIsLoadingBinding(false);
    }
  }, [fetchWithAuth, isAuthenticated, scopeType, scopeId]);

  // Load integrations list
  const loadIntegrations = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoadingIntegrations(true);

    try {
      const res = await fetchWithAuth("/api/crm/integrations?active_only=true");
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data.integrations || []);
      }
    } catch (err) {
      console.error("Error loading CRM integrations:", err);
    } finally {
      setIsLoadingIntegrations(false);
    }
  }, [fetchWithAuth, isAuthenticated]);

  // Load pipelines for an integration
  const loadPipelines = useCallback(
    async (integrationId: string) => {
      if (!isAuthenticated || !integrationId) {
        setPipelines([]);
        setPipelinesError(null);
        return;
      }

      setIsLoadingPipelines(true);

      try {
        const res = await fetchWithAuth(`/api/crm/integrations/${integrationId}/pipelines`);
        const data = await res.json();
        if (res.ok) {
          setPipelines(data.pipelines || []);
          setPipelinesError(null);
        } else {
          setPipelines([]);
          setPipelinesError(data.error || "Failed to load pipelines");
        }
      } catch (err) {
        console.error("Error loading CRM pipelines:", err);
        setPipelines([]);
        setPipelinesError("Failed to load pipelines");
      } finally {
        setIsLoadingPipelines(false);
      }
    },
    [fetchWithAuth, isAuthenticated]
  );

  // Load providers list
  const loadProviders = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/crm/providers");
      if (res.ok) {
        const data = await res.json();
        setProviders(data.providers || []);
      }
    } catch (err) {
      console.error("Error loading CRM providers:", err);
    }
  }, [fetchWithAuth]);

  // Save binding
  const saveBinding = useCallback(
    async (integrationId: string | null, config?: Partial<CrmBindingConfig>) => {
      if (!scopeId) {
        throw new Error(tUi("scopeIDIsRequired"));
      }

      setIsSaving(true);
      setError(null);

      try {
        const res = await fetchWithAuth("/api/crm/bindings/scope", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scopeType,
            scopeId,
            integrationId,
            config: config || binding?.config || {},
          }),
        });

        if (res.ok) {
          const data = await res.json();
          const savedBinding = data.binding as CrmBinding;
          const deliveryAvailabilityChanged =
            hasDeliveryRelevantBindingChanged(binding, savedBinding);
          setBinding(savedBinding);
          if (deliveryAvailabilityChanged) {
            await invalidateDeliverySettings();
          }
          return savedBinding;
        } else {
          const errData = await res.json();
          throw new Error(errData.error || "Failed to save CRM binding");
        }
      } finally {
        setIsSaving(false);
      }
    },
    [binding, fetchWithAuth, invalidateDeliverySettings, scopeId, scopeType, tUi]
  );

  // Update binding config (partial update)
  const updateBindingConfig = useCallback(
    async (configUpdate: Partial<CrmBindingConfig>) => {
      if (!binding) {
        throw new Error(tUi("noBindingToUpdate"));
      }

      const newConfig = {
        ...binding.config,
        ...configUpdate,
      };

      return saveBinding(binding.integrationId, newConfig);
    },
    [binding, saveBinding, tUi]
  );

  // Delete binding (disable CRM)
  const deleteBinding = useCallback(async () => {
    if (!scopeId) return;

    setIsSaving(true);
    setError(null);

    try {
      const res = await fetchWithAuth(
        `/api/crm/bindings/scope?scope_type=${scopeType}&scope_id=${scopeId}`,
        { method: "DELETE" }
      );

      if (res.ok) {
        setBinding(null);
        await invalidateDeliverySettings();
      } else {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to delete CRM binding");
      }
    } finally {
      setIsSaving(false);
    }
  }, [fetchWithAuth, invalidateDeliverySettings, scopeType, scopeId]);

  // Test connection
  const testConnection = useCallback(
    async (integrationId: string) => {
      try {
        const res = await fetchWithAuth(`/api/crm/integrations/${integrationId}/test`, {
          method: "POST",
        });
        const data = await res.json();
        return { success: data.success, error: data.error };
      } catch (_err) {
        return { success: false, error: tUi("connectionTestFailed") };
      }
    },
    [fetchWithAuth]
  );

  // Get stages for a pipeline (from cached pipelines)
  const getStagesForPipeline = useCallback(
    (pipelineId: string): CrmStage[] => {
      const pipeline = pipelines.find((p) => p.id === pipelineId);
      return pipeline?.stages || [];
    },
    [pipelines]
  );

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && isAuthenticated && scopeId) {
      loadBinding();
    }
  }, [autoLoad, isAuthenticated, scopeId, loadBinding]);

  // Load pipelines when binding changes
  useEffect(() => {
    if (binding?.integrationId) {
      loadPipelines(binding.integrationId);
    } else {
      setPipelines([]);
      setPipelinesError(null);
    }
  }, [binding?.integrationId, loadPipelines]);

  // Computed values
  const selectedIntegration = integrations.find((i) => i.id === binding?.integrationId) || null;
  const hasCrmBinding = binding !== null;
  const isLoading = isLoadingBinding || isLoadingIntegrations;
  // Find tenant default integration for display
  const tenantDefaultIntegration = tenantDefault
    ? integrations.find((i) => i.id === tenantDefault.integrationId) || null
    : null;

  return {
    // State
    binding,
    integrations,
    pipelines,
    providers,
    selectedIntegration,
    hasCrmBinding,
    // Tenant default info
    tenantDefault,
    hasTenantDefault,
    tenantDefaultIntegration,

    // Loading states
    isLoading,
    isLoadingBinding,
    isLoadingIntegrations,
    isLoadingPipelines,
    pipelinesError,
    isSaving,
    error,

    // Actions
    loadBinding,
    loadIntegrations,
    loadPipelines,
    loadProviders,
    saveBinding,
    updateBindingConfig,
    deleteBinding,
    testConnection,
    getStagesForPipeline,
  };
}

function getDeliveryScopeType(
  scopeType: CrmScopeType,
): DeliveryScopeType | null {
  switch (scopeType) {
    case "email_agent":
    case "message_agent":
    case "voice_agent":
      return scopeType;
    case "shopify_orders":
      return null;
    default: {
      const exhaustiveScopeType: never = scopeType;
      return exhaustiveScopeType;
    }
  }
}

function hasDeliveryRelevantBindingChanged(
  previousBinding: CrmBinding | null,
  savedBinding: CrmBinding,
): boolean {
  if (previousBinding?.integrationId !== savedBinding.integrationId) {
    return true;
  }

  return isCrmBindingEnabled(previousBinding) !== isCrmBindingEnabled(savedBinding);
}

function isCrmBindingEnabled(binding: CrmBinding | null): boolean {
  return binding !== null && binding.config.enabled !== false;
}
